/**
 * <s-ship-countdown> — per-shopper expedited processing window in the cart.
 *
 * The window (data-window-minutes, default 30, shown from 29:59) starts when the shopper adds a product that was not
 * already in their cart, and only restarts on the next new product. When it runs out, the line hides
 * until a new product is added. State lives in this browser's localStorage, so it is per shopper and
 * survives reloads; it never loops on its own.
 *
 * Each time a window starts, the deadline is written to the cart as an attribute
 * (data-attribute-name, e.g. "Expedited processing by"), so it appears on the order and the store can
 * honor it: an order placed before that time qualifies.
 *
 * Attributes: data-window-minutes, data-variant-ids (comma list of current cart variant IDs),
 * data-attribute-name, data-cart-url (/cart/update.js), data-template ("Order in {time} for ..."),
 * data-locale. Ticks each second with no flashing.
 */
const STORAGE_KEY = 'expedited-window';

const readState = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

const writeState = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage unavailable (private mode); the countdown still runs for this page view.
  }
};

class ShipCountdown extends HTMLElement {
  connectedCallback() {
    this.build();
    this.deadline = this.syncWindow();
    this.render();
    this.timer = setInterval(() => this.render(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  build() {
    const template = this.dataset.template || '{time}';
    const [before = '', after = ''] = template.split('{time}');
    this.timeEl = Object.assign(document.createElement('strong'), { className: 'font-bold tabular-nums text-critical' });
    this.replaceChildren(document.createTextNode(before), this.timeEl, document.createTextNode(after));
  }

  /** Starts a new window only when the cart holds a variant that was not there last time. */
  syncWindow() {
    const ids = (this.dataset.variantIds || '').split(',').filter(Boolean);
    const state = readState();
    const seen = new Set(state.seen || []);
    const hasNewProduct = ids.some((id) => !seen.has(id));
    let deadline = Number(state.deadline) || 0;

    if (hasNewProduct && ids.length > 0) {
      const minutes = Number(this.dataset.windowMinutes || 30);
      deadline = Date.now() + minutes * 60000;
      this.saveDeadlineToCart(deadline);
    }

    // Remember exactly what is in the cart now, so a removed product counts as new if re-added.
    writeState({ seen: ids, deadline });
    return deadline;
  }

  saveDeadlineToCart(deadline) {
    const name = this.dataset.attributeName;
    const url = this.dataset.cartUrl;
    if (!name || !url) return;
    const stamp = new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: this.dataset.timezone || undefined,
      timeZoneName: 'short',
    }).format(new Date(deadline));
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [name]: stamp } }),
    }).catch(() => {});
  }

  render() {
    // Shows 29:59 first for a 30-minute window.
    const remaining = Math.ceil((this.deadline - Date.now()) / 1000) - 1;
    if (remaining < 0) {
      this.hidden = true;
      clearInterval(this.timer);
      return;
    }
    const m = Math.floor(remaining / 60);
    const s = remaining % 60;
    this.timeEl.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    this.hidden = false;
  }
}

if (!customElements.get('s-ship-countdown')) {
  customElements.define('s-ship-countdown', ShipCountdown);
}
